interface DataPoint {
  x: Date;
  y: number;
}

/**
 * Smooths temperature data by detecting and filtering out abnormal spikes
 * while maintaining two decimal precision and ensuring chronological order
 */
export function smoothTemperatureData(data: DataPoint[]): DataPoint[] {
  if (data.length < 5) return data; // Need more points for better context

  // First, ensure data is sorted chronologically
  const sortedData = [...data].sort((a, b) => a.x.getTime() - b.x.getTime());

  // Remove any duplicate timestamps or points too close in time
  const MIN_TIME_DIFF = 10000; // Minimum 10 seconds between readings
  const deduped: DataPoint[] = [];
  
  for (let i = 0; i < sortedData.length; i++) {
    if (i === 0) {
      deduped.push(sortedData[i]);
      continue;
    }

    const timeDiff = sortedData[i].x.getTime() - deduped[deduped.length - 1].x.getTime();
    if (timeDiff >= MIN_TIME_DIFF) {
      deduped.push(sortedData[i]);
    }
  }

  const smoothed: DataPoint[] = [];
  const SPIKE_THRESHOLD = 0.2; // Lower threshold for more aggressive spike detection
  const WINDOW_SIZE = 2; // Look at 2 points before and after

  // Add first two points
  smoothed.push(deduped[0]);
  if (deduped.length > 1) smoothed.push(deduped[1]);

  for (let i = 2; i < deduped.length - 2; i++) {
    const current = deduped[i].y;
    
    // Get surrounding points
    const prevPoints = deduped.slice(Math.max(0, i - WINDOW_SIZE), i).map(p => p.y);
    const nextPoints = deduped.slice(i + 1, Math.min(deduped.length, i + WINDOW_SIZE + 1)).map(p => p.y);
    
    // Calculate average of surrounding points
    const surroundingAvg = [...prevPoints, ...nextPoints].reduce((a, b) => a + b, 0) / (prevPoints.length + nextPoints.length);
    
    // Calculate the trend
    const trend = (nextPoints[0] - prevPoints[0]) / (WINDOW_SIZE * 2);
    const expectedValue = surroundingAvg + trend;
    
    // Check if point deviates significantly from the expected value
    if (Math.abs(current - expectedValue) > SPIKE_THRESHOLD) {
      // Get timestamps for interpolation
      const prevTime = deduped[i - 1].x.getTime();
      const nextTime = deduped[i + 1].x.getTime();
      const currentTime = deduped[i].x.getTime();
      
      // Calculate weighted average based on time distance and trend
      const weight = (currentTime - prevTime) / (nextTime - prevTime);
      const prevValue = prevPoints[prevPoints.length - 1];
      const nextValue = nextPoints[0];
      const interpolatedValue = prevValue + (nextValue - prevValue) * weight + trend;

      smoothed.push({
        x: deduped[i].x,
        y: Number(interpolatedValue.toFixed(2))
      });
    } else {
      smoothed.push({
        x: deduped[i].x,
        y: Number(current.toFixed(2))
      });
    }
  }

  // Add last two points
  if (deduped.length > 3) smoothed.push(deduped[deduped.length - 2]);
  smoothed.push(deduped[deduped.length - 1]);

  return smoothed;
}

/**
 * Creates a smooth battery voltage curve by using a large window moving average
 * and additional smoothing techniques to eliminate oscillations
 */
export function smoothBatteryData(data: DataPoint[]): DataPoint[] {
  if (data.length < 3) return data;

  // First, ensure data is sorted chronologically
  const sortedData = [...data].sort((a, b) => a.x.getTime() - b.x.getTime());

  const smoothed: DataPoint[] = [];
  const WINDOW_SIZE = 30; // Large window for very smooth transitions
  
  // Add first point as is
  smoothed.push(sortedData[0]);

  for (let i = 1; i < sortedData.length; i++) {
    // Get window of points, ensuring we don't exceed array bounds
    const windowStart = Math.max(0, i - WINDOW_SIZE + 1);
    const window = sortedData.slice(windowStart, i + 1);
    
    // Calculate weighted moving average with linear weights
    // More recent points get higher weights
    let weightedSum = 0;
    let weightSum = 0;
    
    for (let j = 0; j < window.length; j++) {
      const weight = j + 1; // Linear weight increasing with recency
      weightedSum += window[j].y * weight;
      weightSum += weight;
    }

    const smoothedValue = weightedSum / weightSum;

    // Additional smoothing: if we have a previous point, limit the rate of change
    if (smoothed.length > 0) {
      const prevValue = smoothed[smoothed.length - 1].y;
      const maxChange = 0.001; // Maximum allowed change between consecutive points
      const change = smoothedValue - prevValue;
      const limitedChange = Math.max(Math.min(change, maxChange), -maxChange);
      
      smoothed.push({
        x: sortedData[i].x,
        y: Number((prevValue + limitedChange).toFixed(3))
      });
    } else {
      smoothed.push({
        x: sortedData[i].x,
        y: Number(smoothedValue.toFixed(3))
      });
    }
  }

  return smoothed;
} 